// src/utils/sortLocationsColumn.js
//
// Shared column-sort helpers for the Locations list table and the nav
// derivation in App.jsx (deriveNavLocations). Extracted from
// LocationsListTable.jsx so both surfaces use the same comparator and
// can never drift.
//
// sortValue  — extract a comparable primitive from a location row for a
//              given sort key. Mirrors the column definitions in
//              LocationsListTable's COLS array.
// applyColumnSort — sort a location array by (sortKey, sortDir) in-place-
//              safe (returns a new array). No-ops when sortKey is falsy.

import { getMAndATagName } from "./mAndA.js";
import { getDisplayedSqft } from "./locationSqftDerived.js";
import { STAGE_ORDER } from "./rankSites.js";

export function sortValue(loc, key) {
  switch (key) {
    case "location":  return (loc.market || "").toLowerCase();
    case "region":    return loc.region || "";
    case "sqft": {
      // LSE-LOCATIONS-SORT-USES-DERIVED-SQFT: use the same derivation the
      // display uses (derivedSqft → leases → stored sqft). -Infinity sentinel
      // places null-sqft rows at bottom for DESC and top for ASC — matches the
      // null-position convention used by other nullable columns.
      const v = getDisplayedSqft(loc, null);
      return v != null ? v : -Infinity;
    }
    case "seats":     return Number(loc.installedCapacity) || 0;
    case "headcount": {
      const hc = loc.latestHeadcount;
      if (!hc || hc.totalCount == null) return -Infinity;
      return Number(hc.totalCount) || 0;
    }
    case "assignedPct": {
      const hc = loc.latestHeadcount;
      const seats = Number(loc.installedCapacity) || 0;
      if (!hc || hc.totalCount == null || seats === 0) return -Infinity;
      return Number(hc.totalCount) / seats;
    }
    case "rent":  return Number(loc.currentYearRent) || 0;
    // For mAndA, return the display tag name for tagged rows; no-tag rows use
    // a high-sentinel path handled in applyColumnSort (see below).
    case "utilizationPct": return loc.avgUtilPct  != null ? Number(loc.avgUtilPct)    : -Infinity;
    // SPACE-FIT-LIST-S3: sort by the computed score; null (no utilization data)
    // uses the -Infinity sentinel so null rows are bottom-pinned in applyColumnSort.
    case "spaceFit":       return loc.spaceFitScore?.score != null ? Number(loc.spaceFitScore.score) : -Infinity;
    case "mAndA": return getMAndATagName(loc.mAndA);
    // Stage: map via STAGE_ORDER (operational=0 → dormant=1 → … → closed=6).
    // Unknown stage values fall to sentinel 99 so they always sort last in
    // both ASC and DESC.
    case "stage": return STAGE_ORDER[loc.stage] ?? 99;
    // SCORE-IN-LIST: "health" is the column key for the Score column (kept for
    // testid stability). scoreTotal is the numeric value from the API's
    // location_score LATERAL join. Null-sentinel rows are bottom-pinned by the
    // pre-check in applyColumnSort — the fallback 0 here is never reached.
    case "health": return loc.scoreTotal != null ? Number(loc.scoreTotal) : 0;
    case "hub":    return loc.isHub ? 1 : 0;
    default:      return "";
  }
}

export function applyColumnSort(locations, sortKey, sortDir = 1) {
  if (!sortKey) return locations;
  return [...locations].sort((a, b) => {
    // mAndA: rows with no tag always land last regardless of sort direction.
    // This must be checked before the generic comparator so DESC doesn't
    // accidentally float no-tag rows to the top.
    if (sortKey === "mAndA") {
      const aEmpty = !a.mAndA;
      const bEmpty = !b.mAndA;
      if (aEmpty && bEmpty) return 0;
      if (aEmpty) return 1;
      if (bEmpty) return -1;
    }
    // SCORE-IN-LIST: non-scored rows (null scoreTotal) always land below all
    // scored rows regardless of sort direction. Mirrors the mAndA bottom-pin
    // pattern above. Ties between two unscored rows resolve by id ascending.
    if (sortKey === "health") {
      const aUnscored = a.scoreTotal == null;
      const bUnscored = b.scoreTotal == null;
      if (aUnscored && bUnscored) return String(a.id || "").localeCompare(String(b.id || ""));
      if (aUnscored) return 1;
      if (bUnscored) return -1;
    }
    // SPACE-FIT-LIST-S3: rows with no spaceFitScore (missing utilization data)
    // always land last regardless of sort direction — same bottom-pin pattern
    // as health/mAndA above.
    if (sortKey === "spaceFit") {
      const aNull = a.spaceFitScore?.score == null;
      const bNull = b.spaceFitScore?.score == null;
      if (aNull && bNull) return String(a.id || "").localeCompare(String(b.id || ""));
      if (aNull) return 1;
      if (bNull) return -1;
    }
    const av = sortValue(a, sortKey);
    const bv = sortValue(b, sortKey);
    if (av < bv) return -sortDir;
    if (av > bv) return sortDir;
    // Universal tiebreak: when two rows compare equal on the primary sort key,
    // resolve by id ascending so the order is always stable and predictable
    // regardless of sort direction or browser sort stability.
    return String(a.id || "").localeCompare(String(b.id || ""));
  });
}
